import * as Sentry from '@sentry/nextjs';
import { saveAs } from 'file-saver';

import { customFetch, downloadUrlAsFile, sendToNative, windowHasNative } from '@/lib/utils';

import {
  PreviewableDataframePayload,
  SignedFileUploadParams,
  TeamFileSignedDownloadParams,
  TeamFileSignedUploadParams,
  TemporaryHubFile,
  UploadToSignedUrlParams
} from './types';

export const getFiles = async ({
  accessToken,
  teamId,
  conversationId
}: {
  accessToken: string;
  teamId?: string;
  conversationId?: string;
}): Promise<TemporaryHubFile[]> => {
  const files: TemporaryHubFile[] = [];
  let cursor: string | null = null;

  do {
    const query = new URLSearchParams();
    if (teamId) query.set('teamId', teamId);
    if (conversationId) query.set('conversationId', conversationId);
    if (cursor) query.set('cursor', cursor);
    const encodedQuery = query.toString();
    const queryString = encodedQuery ? `?${encodedQuery}` : '';

    const res = await customFetch(`/backend/files${queryString}`, {
      headers: {
        Authorization: `Bearer ${accessToken}`
      }
    });

    if (!res.ok) throw new Error('Failed to fetch files');

    const data = (await res.json()) as {
      files?: TemporaryHubFile[];
      next_cursor?: string | null;
    };
    if (!data.files) throw new Error("No 'files' in response JSON");

    files.push(...data.files);
    cursor = data.next_cursor ?? null;
  } while (cursor);

  return teamId
    ? files
    : files.map((file) => ({
        ...file,
        type: 'temporary'
      }));
};

export const getSignedFileUploadUrl = async ({
  accessToken,
  filename,
  fileType
}: SignedFileUploadParams): Promise<{ signedUrl: string; filename: string; mimeType: string }> => {
  const mimeType = fileType === 'application/vnd.ms-excel' ? 'text/csv' : fileType;

  const response = await customFetch('/backend/files', {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${accessToken}`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ filename, mimeType })
  });

  if (!response.ok) throw new Error(`Failed to get signed URL: ${response.statusText}`);

  // The URL is signed for this exact Content-Type; the PUT must send the same
  // value or GCS rejects it with 403 SignatureDoesNotMatch
  const upload = (await response.json()) as { signedUrl: string; filename: string };
  return { ...upload, mimeType };
};

export const getUploadedSpreadsheetPreview = async ({
  accessToken,
  filename,
  extension
}: {
  accessToken: string;
  filename: string;
  extension: string;
}): Promise<PreviewableDataframePayload> => {
  const query = new URLSearchParams({ filename, extension });
  const response = await customFetch(`/backend/files/spreadsheet-preview?${query.toString()}`, {
    headers: {
      Authorization: `Bearer ${accessToken}`
    }
  });

  if (!response.ok) throw new Error(`Failed to get spreadsheet preview: ${response.statusText}`);

  return (await response.json()) as PreviewableDataframePayload;
};

interface SignedTeamURLResponse {
  signed_url: string;
  filepath: string;
}

export const getSignedTeamFileUploadUrl = async ({
  accessToken,
  filepath,
  teamId,
  mimeType
}: TeamFileSignedUploadParams): Promise<SignedTeamURLResponse> => {
  const response = await customFetch(`/backend/teams/${teamId}/files`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${accessToken}`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      filepath,
      mimeType: mimeType || 'application/octet-stream'
    })
  });

  if (!response.ok) throw await response.json();

  return (await response.json()) as SignedTeamURLResponse;
};

export const getSignedTeamFileDownloadUrl = async ({
  accessToken,
  fileId,
  teamId
}: TeamFileSignedDownloadParams): Promise<string> => {
  const response = await customFetch(`/backend/teams/${teamId}/files/${fileId}`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${accessToken}`,
      'Content-Type': 'application/json'
    }
  });

  if (!response.ok) throw await response.json();

  return await response.json();
};

export const uploadToSignedUrl = ({
  signedUrl,
  file,
  filename,
  contentType,
  onProgress
}: UploadToSignedUrlParams) => {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', signedUrl, true);
    // TODO: revert the octet-stream change once file.type works
    xhr.setRequestHeader('Content-Type', contentType);

    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable && onProgress) {
        const percentComplete = Math.round((event.loaded * 100) / event.total);
        onProgress(filename, percentComplete, file.size);
      }
    };

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve(xhr.responseText);
      } else {
        reject(new Error(`Upload Error: ${xhr.status} ${xhr.statusText}`));
      }
    };

    xhr.onerror = () => {
      reject(new Error(`XHR Error: ${xhr.statusText}`));
    };

    xhr.send(file);
  });
};

export const upsertTeamFileMetadata = async ({ accessToken, teamId, filepath }) => {
  const response = await customFetch(`/backend/teams/${teamId}/files/metadata`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${accessToken}`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ object_key: filepath })
  });

  if (!response.ok) throw await response.json();
  return await response.json();
};

export const deleteTeamFile = async ({
  accessToken,
  fileId,
  teamId
}): Promise<{ success: true }> => {
  const response = await customFetch(`/backend/teams/${teamId}/files/${fileId}`, {
    method: 'DELETE',
    headers: {
      'Authorization': `Bearer ${accessToken}`,
      'Content-Type': 'application/json'
    }
  });

  if (!response.ok) throw await response.json();

  return await response.json();
};

export const deleteHubFile = async ({
  filename,
  filepath,
  accessToken
}: {
  filename: string;
  filepath?: string;
  accessToken: string;
}) => {
  const res = await customFetch('/backend/files', {
    headers: {
      'Authorization': `Bearer ${accessToken}`,
      'Content-Type': 'application/json'
    },
    method: 'DELETE',
    body: filepath ? JSON.stringify({ filepath, filename }) : JSON.stringify({ filename })
  });

  if (!res.ok) throw await res.json();

  return await res.json();
};

export const downloadTeamFile = async ({
  accessToken,
  fileId,
  filename,
  teamId
}): Promise<void> => {
  const signedUrl = await getSignedTeamFileDownloadUrl({ accessToken, fileId, teamId });
  const response = await fetch(signedUrl);

  if (!response.ok) throw new Error('Failed to download file');
  const file = await response.blob();

  saveAs(file, filename);
};

export type DownloadFileParams = {
  filename: string;
  accessToken: string;
  conversationId?: string | null;
};

export type FilePreviewMetadata = {
  signedUrl: string;
  artifactId: string | null;
};

export type DownloadFileResult = {
  name: string;
};

export const downloadFile = async ({
  filename,
  accessToken,
  conversationId
}: DownloadFileParams): Promise<DownloadFileResult> => {
  const downloadFilePath = decodeURIComponent(filename);
  const downloadName = downloadFilePath.split('/').pop() || downloadFilePath;
  const query = new URLSearchParams({ filename: downloadFilePath });
  if (conversationId) query.set('conversationId', conversationId);

  const res = await customFetch(`/backend/files/download?${query.toString()}`, {
    headers: {
      Authorization: `Bearer ${accessToken}`
    }
  });

  if (!res.ok) throw new Error('Error downloading file');

  const { signedUrl } = (await res.json()) as { signedUrl: string };

  if (!signedUrl) {
    Sentry.captureMessage('No signedUrl in response', { extra: { filename: downloadFilePath } });
    throw new Error('No signedUrl in response');
  }

  if (windowHasNative()) {
    sendToNative({
      type: 'DOWNLOAD_FILE',
      method: 'signedUrl',
      fileName: downloadName,
      url: signedUrl
    });
  } else {
    const downloaded = await downloadUrlAsFile(signedUrl, downloadName);
    if (!downloaded) throw new Error('Failed to download file');
  }

  return { name: downloadName };
};

// Returns an inline-disposition signed URL for rendering a file in-page
// (e.g. a PDF iframe), unlike downloadFile which forces a browser download.
export const getFilePreviewMetadata = async ({
  filename,
  accessToken,
  conversationId
}: DownloadFileParams): Promise<FilePreviewMetadata> => {
  const decodedFilename = decodeURIComponent(filename);
  const query = new URLSearchParams({ filename: decodedFilename });
  if (conversationId) query.set('conversation_id', conversationId);

  const res = await customFetch(`/backend/files/preview-url?${query.toString()}`, {
    headers: {
      Authorization: `Bearer ${accessToken}`
    }
  });

  if (!res.ok) throw new Error('Error fetching file URL');

  const { signedUrl, artifactId = null } = (await res.json()) as FilePreviewMetadata;

  if (!signedUrl) {
    Sentry.captureMessage('No signedUrl in response', { extra: { filename: decodedFilename } });
    throw new Error('No signedUrl in response');
  }

  return { signedUrl, artifactId };
};

export const getFileSignedUrl = async (params: DownloadFileParams): Promise<string> => {
  const { signedUrl } = await getFilePreviewMetadata(params);
  return signedUrl;
};
